/**
 * Who the agency's money goes to, and which of them it holds a balance for.
 *
 * ## Why this page exists at all, given the picker adds them
 *
 * A beneficiary is added where the charge is recorded, deliberately and always — that is the whole
 * shape of the design and it is in `components/beneficiary-picker.tsx`. This page is the other
 * half of it: something added in three seconds in the middle of a tenancy form is something typed
 * with one hand, and a name that went in as "Okonkwo" needs somewhere to become "Okonkwo &
 * Associates" without a tenancy being drawn up around it. It is where a record is **corrected and
 * retired**, not where one is normally made.
 *
 * ## The light record is the point
 *
 * A lawyer is not forced onto the client list. That was an explicit instruction and it is the
 * reason this screen is not a tab on the clients page: the client list is the people the agency
 * lets *to* and lets *for*, and filling it with every professional who ever drew an agreement makes
 * it unusable for the job it exists to do. So a contact here is a name and a way to reach them, no
 * more — and where a beneficiary genuinely is a client, the row says so and links to the record
 * rather than duplicating it.
 *
 * ## Two things that are refusals rather than omissions
 *
 * The agency's own row cannot be renamed away, archived, or made to accumulate, because a payout
 * against the agency is the agency owing itself money and somebody would eventually try to transfer
 * it. And nothing is deleted: a beneficiary is named on invoices tenants keep and on statements
 * landlords have been sent, and those lines have to go on saying where the money went. Archiving
 * takes it out of the pickers and leaves the record.
 */

import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useSession } from "@/lib/session";
import { api } from "@/lib/api";
import { formValues, nullIfBlank, useApiMutation } from "@/lib/mutations";
import { AddBeneficiaryModal, useBeneficiaries, type Beneficiary } from "@/components/beneficiary-picker";
import { PhoneInput } from "@/components/phone-input";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  Spinner,
  Table,
} from "@/components/ui";

const KIND_LABEL: Record<Beneficiary["kind"], string> = {
  agency: "This agency",
  client: "On the client list",
  contact: "Contact",
};

export function BeneficiariesPage() {
  const { can } = useSession();
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Beneficiary | null>(null);

  const { data, isLoading, error, refetch } = useBeneficiaries({ archived: showArchived });
  const rows = data?.beneficiaries ?? [];
  const mayEdit = can("party.edit");

  const archive = useApiMutation({
    mutationFn: (input: { id: string; archived: boolean }) =>
      api.post(`/beneficiaries/${input.id}/${input.archived ? "restore" : "archive"}`),
    invalidates: [["beneficiaries"]],
  });

  const accruing = rows.filter((row) => row.accrues).length;

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Beneficiaries"
        subtitle={`${rows.length} ${rows.length === 1 ? "record" : "records"} · ${accruing} held to a balance`}
        description={
          "Every charge on a tenancy names who its money is for. This is that list. It is added to from the form " +
          "where a charge is recorded, and corrected or retired here."
        }
        actions={
          mayEdit ? (
            <Button onClick={() => setAdding(true)}>Add a beneficiary</Button>
          ) : null
        }
      />

      {archive.errorMessage ? (
        <div className="mb-4">
          <Alert tone="danger">{archive.errorMessage}</Alert>
        </div>
      ) : null}

      <Card padded={false}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-200 p-4 sm:p-5">
          <CardTitle
            hint={
              "A balance is held only for the ones marked so. A lawyer's agreement fee is usually handed over at the " +
              "time and recorded rather than accumulated — but that is an arrangement, not a rule, and it is set here."
            }
          >
            The list
          </CardTitle>
          <Checkbox
            label="Include retired entries"
            checked={showArchived}
            onChange={(event) => setShowArchived(event.target.checked)}
          />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No beneficiaries."
            hint="One is created for the agency itself the first time a charge needs it."
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th>Contact</th>
                <th>Money held</th>
                <th />
              </tr>
            }
          >
            {rows.map((row) => (
              <tr key={row.id} data-beneficiary-row={row.name}>
                <td>
                  {row.partyId ? (
                    <Link to={`/app/clients/${row.partyId}`} className="link">
                      {row.name}
                    </Link>
                  ) : (
                    <span className="font-medium text-ink-800">{row.name}</span>
                  )}
                  {row.archivedAt ? <span className="block text-xs text-ink-400">Retired</span> : null}
                </td>
                <td className="text-ink-600">{KIND_LABEL[row.kind]}</td>
                <td className="text-ink-500">{row.email || row.phone || "—"}</td>
                <td>
                  {row.accrues ? (
                    <Badge tone="success" dot>
                      Held to a balance
                    </Badge>
                  ) : (
                    <Badge tone="muted">Recorded only</Badge>
                  )}
                </td>
                <td className="num">
                  {mayEdit && row.kind !== "agency" ? (
                    <span className="inline-flex items-center gap-2">
                      <button type="button" className="text-sm text-ink-500 hover:text-ink-800" onClick={() => setEditing(row)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-sm text-ink-500 hover:text-ink-800"
                        disabled={archive.isPending}
                        onClick={() => archive.mutate({ id: row.id, archived: Boolean(row.archivedAt) })}
                      >
                        {row.archivedAt ? "Restore" : "Retire"}
                      </button>
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <AddBeneficiaryModal open={adding} onClose={() => setAdding(false)} onCreated={() => setAdding(false)} />
      <EditBeneficiaryModal beneficiary={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

/**
 * Correcting one.
 *
 * The kind is not offered. A contact cannot become a client record and a client's beneficiary
 * cannot be pointed at somebody else: both would move every invoice line and every statement that
 * already names this row onto a different person, silently and retrospectively. The way to change
 * who money goes to is a new beneficiary and a new choice on the charge.
 */
function EditBeneficiaryModal({ beneficiary, onClose }: { beneficiary: Beneficiary | null; onClose: () => void }) {
  const [accrues, setAccrues] = useState(false);
  const [seeded, setSeeded] = useState<string | null>(null);

  /* Seeded during the render rather than in an effect, because a tick box that arrives one paint
     late is a tick box somebody may have seen in the wrong state. Keyed on the id, so opening a
     second record after the first replaces the answer instead of keeping it. */
  if (beneficiary && seeded !== beneficiary.id) {
    setSeeded(beneficiary.id);
    setAccrues(beneficiary.accrues);
  }

  const save = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch(`/beneficiaries/${beneficiary?.id}`, input),
    invalidates: [["beneficiaries"]],
    onSuccess: () => onClose(),
  });

  if (!beneficiary) return null;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = formValues(event.currentTarget);
    save.mutate({
      name: values.editName,
      email: nullIfBlank(values.editEmail),
      phone: nullIfBlank(values.editPhone),
      accrues,
    });
  };

  return (
    <Modal open onClose={onClose} title={`Edit ${beneficiary.name}`}>
      <form onSubmit={submit} className="space-y-4">
        {save.errorMessage ? <Alert tone="danger">{save.errorMessage}</Alert> : null}

        <Field label="Name" htmlFor="editName" required error={save.fieldErrors.name}>
          <Input id="editName" name="editName" required defaultValue={beneficiary.name} placeholder="Okonkwo & Associates" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email" htmlFor="editEmail" error={save.fieldErrors.email}>
            <Input
              id="editEmail"
              name="editEmail"
              type="email"
              defaultValue={beneficiary.email ?? ""}
              placeholder="them@example.com"
            />
          </Field>
          <Field label="Phone" htmlFor="editPhone">
            <PhoneInput id="editPhone" name="editPhone" defaultValue={beneficiary.phone ?? ""} />
          </Field>
        </div>

        <Checkbox
          id="editAccrues"
          label="Money collected for them builds up until a payout run pays it"
          hint="Turning this off leaves every balance already drawn exactly as it was — it decides what the next run does."
          checked={accrues}
          onChange={(event) => setAccrues(event.target.checked)}
        />

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
